import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { selectUser, fetchUserData, selectUserLoading } from '../../../app/store/slices/userSlice';
import { selectCanvases, fetchUserCanvases } from '../../../app/store/slices/canvasSlice';
import { 
  fetchSubscriptionData, 
  selectCurrentPlan, 
  selectSelectedPlan,
  selectSubcriptionLoading,
} from '../../../app/store/slices/subscriptionSlice';
import ResizableWidgetContainer from '../../shared/components/widgets/ResizableWidgetContainer';

import { 
  fetchUserWidgets, 
  handleCanvasChange, 
  refreshUserWidgetData, 
  syncUserWidgetsWithBackend,
  removeUserWidget,
  loadPersistedWidgets,
  getUserId,
  selectUserWidgets,
  selectUserDashboardLoading, 
  setInitialWidgets,
  saveWidgets,
  fetchUserInsights
} from '../../../app/store/slices/userDashboardSlice';
import { toast } from 'react-toastify';
import { FiLock } from 'react-icons/fi';
import AccountsContent from '../components/dashboard/modules/accounts/AccountsContent';
import IncomeContent from '../components/dashboard/modules/income/IncomeContent';
import BudgetContent from '../components/dashboard/modules/budget/BudgetContent';
import { acceptInvitationThunk } from '../../../app/store/slices/groupSlice';
import CanvasSwitcher from '../components/CanvasSwitcher';
import { useCanvas } from '../context/CanvasContext';
import { verifyInvitationThunk } from '../../../app/store/slices/groupSlice';
import SubscriptionSwitchModal from '../components/SubscriptionSwitchModal';
import AdStatusWidget from '../../shared/components/widgets/AdStatusWidget';
import { checkAdStatus, selectAdStatus } from '../../../app/store/slices/userDashboardSlice';
import { usePrevious } from 'react-use';
import SubscriptionContent from '../components/dashboard/modules/subscription/SubscriptionContent';
import SavingContent from '../components/dashboard/modules/savings/SavingContent';
import DebtContent from '../components/dashboard/modules/debt/DebtContent';
import MortgageContent from '../components/dashboard/modules/mortgage/MortgageContent';
import InvestmentContent from '../components/dashboard/modules/investment/InvestmentContent';
import ResourcesContent from '../components/dashboard/modules/help-and-resources/ResourcesContent';
import HistoryContent from '../components/dashboard/modules/history/HistoryContent';
import { GiMoneyStack } from "react-icons/gi";
import { TbCashRegister } from "react-icons/tb";
import { SiActualbudget } from "react-icons/si";
import {formatAmount} from '../../../utils/formatAmount'
import {FiCreditCard, FiLayers, FiSave, FiHome, FiTrendingUp, FiRepeat } from 'react-icons/fi';
import { TbChartPieFilled } from 'react-icons/tb';
import {GiCash } from "react-icons/gi";


const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useOutletContext();

  const [currentCanvasId, setCurrentCanvasId] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invitationProcessed, setInvitationProcessed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [currentCanvasType, setCurrentCanvasType] = useState('private');
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [invitedPlan, setInvitedPlan] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);
  const [lastWidgetSync, setLastWidgetSync] = useState(null);

  const { userLoading } = useSelector(selectUserLoading);
  const subscriptionLoading = useSelector(selectSubcriptionLoading);
  const selectedPlan = useSelector(selectSelectedPlan);
  const { owned: ownedCanvases, shared: sharedCanvases } = useSelector(selectCanvases) || { owned: [], shared: [] };
  const { selectedCanvas, updateSelectedCanvas } = useCanvas();
  const user = useSelector(selectUser);
  const currentPlan = useSelector(selectCurrentPlan);
  const adStatus = useSelector(selectAdStatus);
  
  const userWidgets = useSelector(selectUserWidgets) || [];
  const widgetsLoading = useSelector(selectUserDashboardLoading) || false;
  const currentCanvasWidgets = userWidgets.filter(widget => 
    widget.canvasId === selectedCanvas?.id
  );  
  const showLoadingOverlay = invitationLoading && !invitationProcessed && isProcessing;
  const PLAN_ORDER = ['free', 'trial', 'single', 'duo', 'family'];
  const getPlanIndex = (planKey) => PLAN_ORDER.indexOf((planKey || '').toLowerCase());

  const insights = useSelector(state => state.userDashboard.insights);
  const insightsLoading = useSelector(state => state.userDashboard.insightsLoading);
  const prevHasAccess = usePrevious(adStatus?.hasAccess);

  const canAccessWidgets = useCallback(() => {
    return adStatus?.isPremium || adStatus?.hasAccess;
  }, [adStatus]);

  const globalLoading = showLoadingOverlay || userLoading || subscriptionLoading;
  const hasAuthData = !!localStorage.getItem("authData");
  const hasSelectedCanvas = !!localStorage.getItem("selectedCanvas");

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchUserInsights(selectedCanvas.id));
      localStorage.removeItem('onboardingCanvas')
    }
  }, [dispatch, selectedCanvas?.id]);

  useEffect(() => {
    if (subscription && currentCanvasId) {
      handleCanvasSwitch(currentCanvasId);
    }
  }, [subscription, currentCanvasId]);

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchUserWidgets({ canvasId: selectedCanvas.id }))
        .then(() => {
          setLastWidgetSync(new Date().toISOString());
        });
    }
  }, [selectedCanvas?.id, dispatch]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (!selectedCanvas?.id) {
        return;
      }
      
      // Get the current widgets directly from state
      const currentWidgets = userWidgets.filter(widget => 
        widget.canvasId === selectedCanvas?.id
      );
      
      if (currentWidgets.length === 0) {
        return;
      }
      // Refresh each widget with error handling
      currentWidgets.forEach(widget => {
        const widgetId = widget._id || widget.id;
        if (widgetId) {
          dispatch(refreshUserWidgetData({ 
            widgetId, 
            canvasId: selectedCanvas.id 
          }))
          .catch(error => {
            console.error('[Widget Refresh] Error refreshing widget:', widgetId, error);
          });
        }
      });
      
      setLastWidgetSync(new Date().toISOString());
    }, 30 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [dispatch, selectedCanvas?.id, userWidgets]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedCanvas?.id) {
        // Try to sync instead of just fetching
        dispatch(syncUserWidgetsWithBackend({ 
          canvasId: selectedCanvas.id 
        }))
        .then((result) => {
          if (result.payload?.synced) {
            setLastWidgetSync(new Date().toISOString());
          } else {
            // Fallback to fetch
            return dispatch(fetchUserWidgets({
              canvasId: selectedCanvas.id,
              forceRefresh: true
            }));
          }
        })
        .then(() => {
          setLastWidgetSync(new Date().toISOString());
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, selectedCanvas?.id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user data
        await dispatch(fetchUserData()).unwrap();
        //fetch Canvas Data
        await dispatch(fetchUserCanvases()).unwrap();

        // Fetch subscription data
        const result = await dispatch(fetchSubscriptionData()).unwrap();
       
      //   if((!result?.data || !result?.data ===null) && user?.role === 'member' || user?.role === 'manager') {
      //     toast.info('Subscribe to plan First')
      //     navigate('/user-subscription')
      //   }
        if(result?.data?.status==='incomplete' && result?.data?.paymentStatus==='pending') {
          toast.info('Your subscription is incomplete. Please complete the payment to activate your plan.');
          navigate('/payment', {
            state: {
              subscriptionId: result.data.subscriptionId,
              planDetails: result.data.plan,
              selectedPlanKey: result.data.plan.planKey,
              billingCycle: result.data.billingCycle
            },
            replace: true
          });
          toast.success('Subscription paid Successfully!');
          navigate('/dashboard');
          return;
        }
         setSubscription(result?.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    const processPendingInvitation = async () => {
      const pendingInvitationData = localStorage.getItem('pendingInvitation');
      if (!pendingInvitationData || !user || isProcessing || invitationProcessed) {
        return;
      }

      try {
        // Parse the invitation data
        let { token, email } = JSON.parse(pendingInvitationData);
        let invitationPlanKey = null;
        let invitationPlanName = null;
        if (!email) {
          const result = await dispatch(verifyInvitationThunk(token)).unwrap();
          if (!result?.data?.email) {
            toast.error('Invalid invitation');
            localStorage.removeItem('pendingInvitation');
            return;
          } else {
            email = result?.data?.email;
            invitationPlanKey = result?.data?.planKey;
            invitationPlanName = result?.data?.planName || invitationPlanKey;
          }
        }

        if (
          email.trim().toLowerCase() !== user.email.trim().toLowerCase()
        ) {
          toast.error('This invitation is for a different email address');
          localStorage.removeItem('pendingInvitation');
          return;
        }

        setIsProcessing(true);
        setInvitationLoading(true);

        if (currentPlan && currentPlan?.status === 'active') {
          const invitedIndex = getPlanIndex(invitationPlanKey);
          const currentIndex = getPlanIndex(currentPlan?.plan?.planKey);

          if (invitedIndex > currentIndex) {
            // Invited plan is higher, show modal
            setInvitedPlan({ planKey: invitationPlanKey, name: invitationPlanName });
            setPendingToken(token);
            setShowSwitchModal(true);
            setIsProcessing(false);
            setInvitationLoading(false);
            return;
          } else {
            localStorage.removeItem('pendingInvitation');
            toast.error('You have already an Active Subscription');
            setIsProcessing(false);
            setInvitationLoading(false);
            return;
          }
        } else {
          let result = await dispatch(acceptInvitationThunk(token.trim())).unwrap();
            if (result?.status === 'already_member') {
            setInvitedPlan({ planKey: invitationPlanKey, name: invitationPlanName });
            setPendingToken(token);
            setShowSwitchModal(true);
            setIsProcessing(false);
            setInvitationLoading(false);
            setInvitationProcessed(false);
            toast.info(result.message || 'You are already a member of a subscription.');
            return;
          }

          toast.success('Invitation accepted successfully!', {
            toastId: 'invitation-success',
          });
          localStorage.removeItem('pendingInvitation');
        }
      } catch (error) {
        console.error('Accept invitation error:', error);
        toast.error(error);
        localStorage.removeItem('pendingInvitation');
      } finally {
        setIsProcessing(false);
        setInvitationLoading(false);
        setInvitationProcessed(true);
      }
    };

    if (user?.email) {
      processPendingInvitation();
    }
  }, [dispatch, user, isProcessing, invitationProcessed, currentPlan]);

    // Listen for widget changes in state
  useEffect(() => {
    if (selectedCanvas?.id && userWidgets.length > 0) {
      const filteredWidgets = userWidgets.filter(widget => 
        widget.canvasId === selectedCanvas.id
      );
      
      // If we have widgets for this canvas, update last sync time
      if (filteredWidgets.length > 0) {
        setLastWidgetSync(new Date().toISOString());
      }
    }
  }, [userWidgets, selectedCanvas?.id]); 

  // automatically load widgets when the component mounts
  useEffect(() => {
    if (selectedCanvas?.id) {
      
      // First try to load from localStorage for immediate display
      const userId = getUserId();
      const persistedWidgets = loadPersistedWidgets(userId, selectedCanvas.id);
      
      if (persistedWidgets.length > 0) {
        
        // Immediately display cached widgets
        dispatch(setInitialWidgets({
          widgets: persistedWidgets,
          canvasId: selectedCanvas.id
        }));
        
        // Set timestamp from last sync
        const lastSync = localStorage.getItem(`last_sync_${selectedCanvas.id}`);
        if (lastSync) {
          setLastWidgetSync(new Date(parseInt(lastSync)).toISOString());
        }
      }
      
      // Then fetch fresh data from the server
      dispatch(fetchUserWidgets({ 
        canvasId: selectedCanvas.id,
        forceRefresh: true
      }))
      .then(() => {
        setLastWidgetSync(new Date().toISOString());
      })
      .catch(error => {
        console.error("Error loading widgets on mount:", error);
      });
    }
  }, [selectedCanvas?.id, dispatch]);

  // Run once on app init to check and migrate storage
  useEffect(() => {
    const userId = getUserId();
    
    // Check if we need to migrate old widget data to canvas-specific storage
    if (localStorage.getItem('user_widgets')) {
      try {
        const oldWidgets = JSON.parse(localStorage.getItem('user_widgets'));
        
        // Group widgets by canvas
        const widgetsByCanvas = {};
        oldWidgets.forEach(widget => {
          const canvasId = widget.canvasId;
          if (canvasId) {
            if (!widgetsByCanvas[canvasId]) {
              widgetsByCanvas[canvasId] = [];
            }
            widgetsByCanvas[canvasId].push(widget);
          }
        });
        
        // Save each canvas's widgets to its own storage key
        Object.keys(widgetsByCanvas).forEach(canvasId => {
          saveWidgets(widgetsByCanvas[canvasId], userId, canvasId);
        });
        
        // Remove old storage
        localStorage.removeItem('user_widgets');
      } catch (error) {
        console.error('Error migrating old widget data:', error);
      }
    }
  }, []);

    useEffect(() => {
    if (
      selectedCanvas?.id &&
      adStatus?.hasAccess &&
      prevHasAccess === false // only when it changes from false to true
    ) {
      dispatch(fetchUserWidgets({ canvasId: selectedCanvas.id, forceRefresh: true }));
    }
  }, [adStatus?.hasAccess, selectedCanvas?.id, dispatch, prevHasAccess]);

  // useEffect to check ad status periodically
  useEffect(() => {
    // Check ad status on component mount
    dispatch(checkAdStatus(selectedCanvas?.id));
    
    // Check ad status every 30 seconds
    const adStatusInterval = setInterval(() => {
      dispatch(checkAdStatus(selectedCanvas?.id));
    }, 30000);
    
    return () => clearInterval(adStatusInterval);
  }, [dispatch]);
  

    // Handler for modal confirm
  const handleSwitchConfirm = async () => {
    setShowSwitchModal(false);
    setIsProcessing(true);
    setInvitationLoading(true);
    try {
      await dispatch(acceptInvitationThunk({ token: pendingToken, forceSwitch: true })).unwrap();
      dispatch(fetchSubscriptionData())
      toast.success('Invitation accepted and plan switched!', {
        toastId: 'invitation-success',
      });
      localStorage.removeItem('pendingInvitation');
    } catch (error) {
      toast.error(error);
    } finally {
      setIsProcessing(false);
      setInvitationLoading(false);
      setInvitationProcessed(true);
    }
  };

  // Handler for modal cancel
  const handleSwitchCancel = () => {
    setShowSwitchModal(false);
    setPendingToken(null);
    setInvitedPlan(null);
    localStorage.removeItem('pendingInvitation');
    toast.info('Stayed with your current subscription.');
  };

  const handleCanvasSwitch = (canvasId) => {
    // Skip if already on this canvas
    if (currentCanvasId === canvasId) {
      return;
    }
    
    setCurrentCanvasId(canvasId);
    const selectedCanvas = [...ownedCanvases, ...sharedCanvases].find(canvas => canvas._id === canvasId);
    
    if (selectedCanvas) {
      // Show loading toast
      const loadingToastId = toast.loading(`Loading ${selectedCanvas.name} canvas...`);
      
      // First clear the widgets for clean state transition
      dispatch(handleCanvasChange({ canvasId, clearWidgets: true }));
      
      // Update canvas context
      setCurrentCanvasType(selectedCanvas.type);
      updateSelectedCanvas(selectedCanvas);
      
      // Try to load cached widgets first for immediate display
      const userId = getUserId();
      const persistedWidgets = loadPersistedWidgets(userId, canvasId);
      
      if (persistedWidgets.length > 0) {
        // Display cached widgets immediately
        dispatch(setInitialWidgets({
          widgets: persistedWidgets,
          canvasId: canvasId
        }));
      }
      
      // Then fetch fresh data
      dispatch(fetchUserWidgets({ 
        canvasId, 
        forceRefresh: true 
      }))
      .then(() => {
        setLastWidgetSync(new Date().toISOString());
        
        toast.update(loadingToastId, {
          render: `Switched to ${selectedCanvas.name} canvas`,
          type: "success",
          isLoading: false,
          autoClose: 2000
        });
      })
      .catch(error => {        
        toast.update(loadingToastId, {
          render: `Error loading widgets for ${selectedCanvas.name}`,
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
      });
    }
  };
  

  const handlePlanNoticeClick = () => {
    if (!currentPlan?.plan?.planKey) {
      toast.error('Unable to determine current plan');
      return;
    }

    switch (currentPlan?.plan?.planKey.toLowerCase()) {
      case 'free':
        toast.error('Switch to Other Plans for Smooth Experience', {
          toastId: 'plan-change-free'
        });
        navigate('/user-subscription');
        break;

      case 'single':
        toast.info('You are already on the Single Plan\nWe are working on the Multi-Plan feature', {
          toastId: 'plan-change-single'
        });
        break;

      case 'duo':
        toast.info('You are already on the Duo Plan\nWe are working on the Multi-Plan feature', {
          toastId: 'plan-change-duo'
        });
        break;

      case 'trial':
        toast.info('You are already on the Trial Plan\nSwitch to a Paid Plan', {
          toastId: 'plan-change-trial'
        });
        navigate('/user-subscription');
        break;

      case 'family':
        toast.info('You are already on the Family Plan\nIt is a premium plan', {
          toastId: 'plan-change-family'
        });
        break;

      default:
        toast.info('Unknown plan type', {
          toastId: 'plan-change-unknown'
        });
    }
  };

  const NoSubscriptionNotice = () => {
    return (
      <>
       {!subscriptionLoading &&(
         <span
        className="text-blue-600 underline cursor-pointer"
        onClick={handlePlanNoticeClick}
        tabIndex={0}
        role="button"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') handlePlanNoticeClick();
        }}
      >
        {selectedPlan?.plan?.name || currentPlan?.plan?.name || 'No Subscription Exists'}
      </span>
        )}
        </>
    );
  };

  const renderContent = () => {
    switch(activeSection) {
      case 'accounts':
        return <AccountsContent />;
      case 'income':
        return <IncomeContent />;
      case 'budget':
        return <BudgetContent/>
      case 'subscriptions':
        return <SubscriptionContent/>
      case 'savings':
        return <SavingContent/>
      case 'debts':
        return <DebtContent/>
      case 'mortgage':
        return <MortgageContent/>
      case 'investments':
        return <InvestmentContent/>
      case 'help & resources':
        return <ResourcesContent/>
      case 'history':
        return <HistoryContent/>
      
      default:
        return (
          <div className="space-y-6">

            {insightsLoading ? (
              <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100 flex justify-center items-center min-h-[200px]">
                <div className="h-8 w-8 rounded-full border-t-2 border-b-2 border-blue-600 animate-spin"></div>
              </div>
            ) : insights ? (
              <div className="mb-8 space-y-8">
                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 rounded-xl p-5 flex flex-col items-center shadow border border-green-100"
                    onClick={() => setActiveSection('accounts')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                  >
                    <GiMoneyStack className="text-green-500 text-3xl mb-2" />
                    <div className="text-2xl font-bold text-green-900">{formatAmount(insights?.totalBalance)}</div>
                    <div className="text-xs text-green-700">Total Balance</div>
                  </div>

                  <div className="bg-yellow-50 rounded-xl p-5 flex flex-col items-center shadow border border-yellow-100"
                    onClick={() => setActiveSection('income')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                  >
                    <TbCashRegister className="text-yellow-500 text-3xl mb-2" />
                    <div className="text-2xl font-bold text-yellow-900">{formatAmount(insights?.totalIncome)}</div>
                    <div className="text-xs text-yellow-700">Monthly Income</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-5 flex flex-col items-center shadow border border-blue-100"
                    onClick={() => setActiveSection('budget')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                  >
                    <TbChartPieFilled className="text-blue-500 text-3xl mb-2" />
                    <div className="text-2xl font-bold text-blue-900">{formatAmount(insights?.totalBudgeted)}</div>
                    <div className="text-xs text-blue-700">Budgeted</div>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-5 flex flex-col items-center shadow border border-indigo-100"
                    onClick={() => setActiveSection('savings')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                  >
                    <GiCash className="text-indigo-500 text-3xl mb-2" />
                    <div className="text-2xl font-bold text-indigo-900">{formatAmount(insights.totalSaved)}</div>
                    <div className="text-xs text-indigo-700">Total Saved</div>
                  </div>
                </div>

                  {/* Mini Grid*/}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center border border-gray-100"
                        onClick={() => {
                        setActiveSection('settings');
                        navigate('/settings', { state: { activeTab: 'manage-canvas' } });
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Go to Manage Canvas Settings"
                    >
                      <FiLayers className="text-blue-500 text-2xl mb-2" />
                      <div className="text-lg font-bold">{insights.totalCanvases || 0}</div>
                      <div className="text-xs text-gray-500">Total Canvases</div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center border border-gray-100"
                    onClick={() => setActiveSection('mortgage')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                    >
                      <FiHome className="text-pink-500 text-2xl mb-2" />
                      <div className="text-lg font-bold">{formatAmount(insights?.totalMortgage)}</div>
                      <div className="text-xs text-gray-500">Mortgage</div>
                    </div>

                    {/* Property Equity */}
                    <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center border border-gray-100"
                    onClick={() => setActiveSection('mortgage')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                    >
                      <FiHome className="text-green-400 text-2xl mb-2" />
                      <div className="text-lg font-bold">{formatAmount(insights?.totalPropertyEquity)}</div>
                      <div className="text-xs text-gray-500">Property Equity</div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center border border-gray-100"
                    onClick={() => setActiveSection('investments')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                    >
                      <FiTrendingUp className="text-purple-500 text-2xl mb-2" />
                      <div className="text-lg font-bold">{formatAmount(insights?.totalPortfolioValue)}</div>
                      <div className="text-xs text-gray-500">Investments</div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center border border-gray-100"
                    onClick={() => setActiveSection('debts')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                    >
                      <FiCreditCard className="text-yellow-500 text-2xl mb-2" />
                      <div className="text-lg font-bold">{formatAmount(insights?.totalDebts)}</div>
                      <div className="text-xs text-gray-500">Debts</div>
                    </div>
                    {/*Subscriptions*/}
                    <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center border border-gray-100"
                    onClick={() => setActiveSection('subscriptions')}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to Accounts"
                    >
                      <FiRepeat className="text-yellow-400 text-2xl mb-2" />
                      <div className="text-lg font-bold">{formatAmount(insights?.totalSubscriptions)}</div>
                      <div className="text-xs text-gray-500">Subscriptions</div>
                    </div>
                  </div>
                </div>
            ) : null}
                          
            {/* If no widgets available, show empty state */}
            {canAccessWidgets() && !widgetsLoading && userWidgets.length === 0 ? (
              <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h2 className="text-lg font-semibold text-[#0a363c] mb-3">No Insights Available</h2>
                <p className="text-gray-500 mb-5">Use CashBot to create widgets and gain insights about your finances.</p>
                <div className="bg-gray-50 p-3 rounded-lg max-w-md mx-auto">
                  <p className="text-sm text-gray-600 font-medium mb-2">Try asking CashBot:</p>
                  <div className="space-y-1 text-gray-500 text-sm">
                    <p>"Show my monthly expenses"</p>
                    <p>"Create a budget tracker widget"</p>
                    <p>"Analyze my spending patterns"</p>
                  </div>
                </div>
              </div>
            )  
            : (
              <div className="space-y-6">
                {/* Canvas Widget Access Check */}
                {canAccessWidgets() ? (
                  <>
                    {/* Resizable Widget Grid - Always show if widgets exist */}
                    {currentCanvasWidgets.length > 0 && (
                      <div className="bg-white p-4 md:p-6 rounded-lg shadow">
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                              {selectedCanvas?.name || 'Canvas'} Widgets
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Drag to reposition • Resize from bottom-right corner • Click "Save Layout" to save changes
                            </p>
                          </div>
                          <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {currentCanvasWidgets.length} Active Widget{currentCanvasWidgets.length !== 1 ? 's' : ''}
                          </div>
                        </div>

                        {/* Resizable Widget Container */}
                        <ResizableWidgetContainer
                          widgets={currentCanvasWidgets}
                          context="user"
                          canvasId={selectedCanvas?.id}
                          enableResize={true}
                          enableDrag={true}
                          compact={false}
                        />
                      </div>
                    )}

                    {/* Empty state for no widgets */}
                    {currentCanvasWidgets.length === 0 && (
                      <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-100">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h2 className="text-lg font-semibold text-[#0a363c] mb-3">No Widgets Available</h2>
                        <p className="text-gray-500 mb-5">Use CashBot to create widgets and gain insights about your finances.</p>
                        <div className="bg-gray-50 p-3 rounded-lg max-w-md mx-auto">
                          <p className="text-sm text-gray-600 font-medium mb-2">Try asking CashBot:</p>
                          <div className="space-y-1 text-gray-500 text-sm">
                            <p>"Show my monthly expenses"</p>
                            <p>"Create a budget tracker widget"</p>
                            <p>"Analyze my spending patterns"</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center mx-auto max-w-2xl w-full">                     
                    <FiLock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">
                      Widget Access Locked
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Watch ads to unlock access to your widgets or upgrade to a premium plan.
                    </p>
                    <AdStatusWidget />
                  </div>
                )}
              </div>
            )}
          </div>
        );
    }
  };


  
  if (!globalLoading && (!hasAuthData && !hasSelectedCanvas)) {
    try {
      localStorage.removeItem('authData');
      localStorage.removeItem('canvasData');
      localStorage.removeItem('selectedCanvas');
      localStorage.removeItem('pendingInvitation');
      toast.info('Session expired. Redirecting to login...');
    } catch (e) {
      console.error('Error clearing session on logout:', e);
    }
    navigate('/login', { replace: true });
    return null;
  }


  if (globalLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl text-center max-w-sm w-full mx-4">
          <div className="mb-4">
            <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-blue-600 animate-spin mx-auto"></div>
          </div>
          <p className="text-gray-700 font-medium">Loading...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we set things up</p>
        </div>
      </div>
    );
  }


  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 md:p-6">
      {/* Header Section */}
      <div className="text-center mb-4 md:mb-8">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-2">
          <h1 className="text-xl md:text-2xl font-bold text-btn-primary break-words">
            {user?.name || 'User'}'s Cash Canvas
          </h1>
          {user?.role !== 'kid' && (
            <div className="flex items-center gap-2">
              <CanvasSwitcher 
                onCanvasSwitch={handleCanvasSwitch}
                currentCanvasId={currentCanvasId}
              />
            </div>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Plan: <span className="font-medium"><NoSubscriptionNotice /></span>
        </p>
      </div>

      {/* Dynamic Content */}
      {renderContent()}

      {/* SwitchSubscription */}
      <SubscriptionSwitchModal
        open={showSwitchModal}
        currentPlan={currentPlan}
        invitedPlan={invitedPlan}
        onConfirm={handleSwitchConfirm}
        onCancel={handleSwitchCancel}
      />
    </div>
  );
};

export default DashboardHome;
